import { useEffect, useId, useRef, useState } from 'react';

import { Icon } from '@/components/icon';
import { addDays, displayRange, rangeForPreset, type RangePreset } from '@/lib/date-range';

const presets: { id: Exclude<RangePreset, 'custom'>; label: string }[] = [
  { id: 'today', label: 'Today' }, { id: '7d', label: 'Last 7 days' }, { id: '30d', label: 'Last 30 days' },
  { id: 'last-month', label: 'Last month' }, { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' }, { id: 'all', label: 'All time' },
];

function presetLabel(preset: RangePreset) {
  return presets.find((option) => option.id === preset)?.label ?? 'Custom range';
}

export function DateRangeMenu(props: {
  preset: RangePreset;
  from: string;
  toExclusive: string;
  allowAllTime?: boolean;
  onPreset: (value: Exclude<RangePreset, 'custom'>) => void;
  onCustomRange: (from: string, toExclusive: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(props.from);
  const [customThrough, setCustomThrough] = useState(addDays(props.toExclusive, -1));
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const visiblePresets = presets.filter((preset) => props.allowAllTime !== false || preset.id !== 'all');
  const validCustomRange = Boolean(customFrom && customThrough && customFrom <= customThrough);

  useEffect(() => {
    setCustomFrom(props.from);
    setCustomThrough(addDays(props.toExclusive, -1));
  }, [props.from, props.toExclusive]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePress = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return <div className="date-range-menu" ref={menuRef}>
    <button
      className="date-range-menu__trigger"
      type="button"
      aria-expanded={open}
      aria-controls={menuId}
      aria-haspopup="dialog"
      onClick={() => setOpen((current) => !current)}
    >
      <i className="date-range-menu__marker" />
      <span className="date-range-menu__current">{presetLabel(props.preset)}</span>
      <span className="date-range-menu__summary">{displayRange(props.from, props.toExclusive)}</span>
      <Icon name="chevron-down" size={18} />
    </button>
    {open ? <section className="date-range-menu__popover" id={menuId} aria-label="Date range" role="dialog">
      <div className="date-range-menu__options" role="group" aria-label="Date range presets">
        {visiblePresets.map((option) => {
          const range = rangeForPreset(option.id);
          return <button
            className={props.preset === option.id ? 'is-selected' : ''}
            type="button"
            key={option.id}
            onClick={() => { props.onPreset(option.id); setOpen(false); }}
          >
            <strong>{option.label}</strong><span>{displayRange(range.from, range.toExclusive)}</span>
          </button>;
        })}
      </div>
      <div className="date-range-menu__divider" />
      <p className="date-range-menu__custom-label">Custom range</p>
      <div className="date-range-menu__custom-fields">
        <label><span>From</span><input aria-label="Custom range from" type="date" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} /></label>
        <Icon name="arrow-right" size={18} />
        <label><span>Through</span><input aria-label="Custom range through" type="date" value={customThrough} onChange={(event) => setCustomThrough(event.target.value)} /></label>
      </div>
      <button
        className="date-range-menu__apply"
        type="button"
        disabled={!validCustomRange}
        onClick={() => {
          props.onCustomRange(customFrom, addDays(customThrough, 1));
          setOpen(false);
        }}
      >Apply custom range</button>
    </section> : null}
  </div>;
}
